import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { FiCopy, FiMaximize2, FiMinimize2 } from "react-icons/fi";
import { MarkdownRenderer } from "../../components/MarkdownRenderer";
import fixturesManifest from "../../../../packages/lib/__tests__/fixtures/fixtures.json";

type ViewMode = "markdown" | "rendered" | "html" | "clean" | "preview";

export const Route = createFileRoute("/dev/web-scrape")({
	validateSearch: (search: Record<string, unknown>) => {
		const key = typeof search.key === "string" ? search.key : undefined;
		const validViews = new Set<ViewMode>([
			"markdown",
			"rendered",
			"html",
			"clean",
			"preview",
		]);
		const view: ViewMode = validViews.has(search.view as ViewMode)
			? (search.view as ViewMode)
			: "markdown";
		const fullscreen =
			search.fullscreen === true || search.fullscreen === "true";

		return { key, view, fullscreen };
	},
	component: WebScrape,
});

const PRESETS = Object.entries(fixturesManifest).map(([key, url]) => ({
	label: key
		.split("-")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" "),
	url,
}));

const VIEW_MODE_LABELS: Record<ViewMode, string> = {
	markdown: "Markdown",
	rendered: "Rendered",
	clean: "Clean HTML",
	preview: "Preview",
	html: "Raw HTML",
};

interface ScrapeData {
	url: string;
	title: string | null;
	meta: Record<string, string>;
	markdown: string;
	html: string;
	cleanHtml: string;
	key: string;
	cached: boolean;
}

interface ScrapeResult {
	status: "idle" | "loading" | "success" | "error";
	data?: ScrapeData;
	timing_ms?: number;
	error?: string;
}

function WebScrape() {
	const { key: searchKey, view, fullscreen } = Route.useSearch();
	const navigate = Route.useNavigate();
	const [inputUrl, setInputUrl] = useState("");
	const [result, setResult] = useState<ScrapeResult>({ status: "idle" });
	const [useCache, setUseCache] = useState(true);
	const [aggressive, setAggressive] = useState(false);

	function setFullscreen(fs: boolean) {
		navigate({ search: (prev) => ({ ...prev, fullscreen: fs }) });
	}

	async function doFetch(param: string, by: "url" | "key") {
		setResult({ status: "loading" });
		const noCache = by === "url" && !useCache ? "&no_cache=1" : "";
		try {
			const res = await fetch(
				`/dev-api/web-scrape?${by}=${encodeURIComponent(param)}${noCache}`,
			);
			const json = await res.json();
			if (!res.ok || json.error) {
				setResult({
					status: "error",
					error: json.error || `HTTP ${res.status}`,
					timing_ms: json.timing_ms,
				});
			} else {
				setResult({
					status: "success",
					data: json.data,
					timing_ms: json.timing_ms,
				});
				// Update search param to the cache key
				if (json.data.key && json.data.key !== searchKey) {
					navigate({
						search: (prev) => ({ ...prev, key: json.data.key }),
					});
				}
			}
		} catch (err) {
			setResult({
				status: "error",
				error: err instanceof Error ? err.message : String(err),
			});
		}
	}

	// Auto-fetch from cache key on page load / refresh
	useEffect(() => {
		if (searchKey) doFetch(searchKey, "key");
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Close fullscreen on Escape
	useEffect(() => {
		if (!fullscreen) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setFullscreen(false);
		}
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [fullscreen]);

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!inputUrl.trim()) return;
		doFetch(inputUrl, "url");
	}

	function handlePreset(presetUrl: string) {
		setInputUrl(presetUrl);
		doFetch(presetUrl, "url");
	}

	function setView(mode: ViewMode) {
		navigate({ search: (prev) => ({ ...prev, view: mode }) });
	}

	function getContentText(): string | undefined {
		if (!result.data) return undefined;
		if (view === "markdown" || view === "rendered")
			return result.data.markdown;
		if (view === "clean" || view === "preview") {
			const html = result.data.cleanHtml;
			return aggressive ? stripAttributes(html) : html;
		}
		return result.data.html;
	}
	const contentText = getContentText();

	const viewToggle = (
		<div className="flex items-center gap-2">
			<div className="flex gap-1 rounded bg-white/5 p-0.5">
				{(
					[
						"markdown",
						"rendered",
						"clean",
						"preview",
						"html",
					] as const
				).map((mode) => (
					<button
						key={mode}
						onClick={() => setView(mode)}
						className={`rounded px-2 py-0.5 text-xs capitalize ${view === mode ? "bg-white/10 font-medium" : "text-muted hover:text-foreground"}`}
					>
						{VIEW_MODE_LABELS[mode]}
					</button>
				))}
			</div>
			{contentText && view !== "preview" && view !== "rendered" && (
				<CopyButton text={contentText} />
			)}
			{contentText && (view === "clean" || view === "preview") && (
				<>
					<label className="text-muted flex items-center gap-1 text-xs select-none">
						<input
							type="checkbox"
							checked={aggressive}
							onChange={(e) => setAggressive(e.target.checked)}
							className="accent-yellow-500"
						/>
						Strip attrs
					</label>
					<span className="text-muted text-xs tabular-nums">
						{contentText.length.toLocaleString()} chars ~
						{Math.round(contentText.length / 4).toLocaleString()}{" "}
						tokens
					</span>
				</>
			)}
			<button
				onClick={() => setFullscreen(!fullscreen)}
				className="text-muted hover:text-foreground p-1"
				title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
			>
				{fullscreen ? (
					<FiMinimize2 size={14} />
				) : (
					<FiMaximize2 size={14} />
				)}
			</button>
		</div>
	);

	if (fullscreen && contentText) {
		return (
			<div className="fixed inset-0 z-50 flex flex-col bg-black">
				<div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
					<h2 className="text-sm font-medium">Content</h2>
					{viewToggle}
				</div>
				{view === "rendered" ? (
					<div className="flex-1 overflow-auto p-6">
						<MarkdownRenderer markdown={contentText} />
					</div>
				) : view === "preview" ? (
					<iframe
						srcDoc={stripImages(contentText)}
						sandbox=""
						className="flex-1 bg-white"
						title="Preview"
					/>
				) : (
					<div className="flex-1 overflow-auto p-4">
						<pre className="text-xs leading-relaxed">
							<code className="break-words whitespace-pre-wrap">
								{contentText}
							</code>
						</pre>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-5xl px-6 py-8">
			<h1 className="mb-1 text-2xl font-semibold">Web Scrape</h1>
			<p className="text-muted mb-6 text-sm">
				Fetch any URL via ScrapingFish, extract meta tags, and convert
				to markdown.
			</p>

			<form onSubmit={handleSubmit} className="mb-4 flex gap-2">
				<input
					type="url"
					value={inputUrl}
					onChange={(e) => setInputUrl(e.target.value)}
					placeholder="https://example.com/..."
					className="flex-1 rounded border border-white/10 bg-white/5 px-3 py-2 text-sm placeholder:text-white/30 focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/30 focus:outline-none"
				/>
				<label className="text-muted flex items-center gap-1.5 text-xs select-none">
					<input
						type="checkbox"
						checked={useCache}
						onChange={(e) => setUseCache(e.target.checked)}
						className="accent-yellow-500"
					/>
					Cache
				</label>
				<button
					type="submit"
					disabled={!inputUrl.trim() || result.status === "loading"}
					className="rounded bg-yellow-500 px-4 py-2 text-sm font-medium text-black hover:bg-yellow-400 disabled:opacity-40"
				>
					{result.status === "loading" ? "Fetching..." : "Fetch"}
				</button>
			</form>

			<div className="mb-8 flex flex-wrap gap-2">
				{PRESETS.map((p) => (
					<button
						key={p.url}
						onClick={() => handlePreset(p.url)}
						className="rounded border border-white/10 px-2.5 py-1 text-xs hover:border-white/20 hover:bg-white/5"
					>
						{p.label}
					</button>
				))}
			</div>

			{result.status === "loading" && (
				<p className="text-muted animate-pulse text-sm">Loading...</p>
			)}

			{result.status === "error" && (
				<div className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3">
					<p className="text-sm text-red-400">{result.error}</p>
					{result.timing_ms != null && (
						<p className="text-muted mt-1 text-xs">
							{result.timing_ms}ms
						</p>
					)}
				</div>
			)}

			{result.status === "success" && result.data && (
				<>
					<div className="space-y-6">
						<div className="text-muted text-xs">
							<a
								href={result.data.url}
								target="_blank"
								rel="noreferrer"
								className="hover:text-foreground underline"
							>
								{result.data.url}
							</a>
							{result.timing_ms != null && (
								<span className="ml-2">
									—{" "}
									{result.data.cached ? "cached" : "fetched"}{" "}
									in {result.timing_ms}ms
								</span>
							)}
						</div>

						<div className="rounded border border-white/10">
							<div className="border-b border-white/10 px-4 py-2">
								<h2 className="text-sm font-medium">
									Title & Meta Tags
								</h2>
							</div>
							<div className="p-4">
								{result.data.title && (
									<p className="mb-3 text-sm font-medium">
										{result.data.title}
									</p>
								)}
								{Object.keys(result.data.meta).length > 0 ? (
									<table className="w-full text-sm">
										<thead>
											<tr className="border-b border-white/10 text-left">
												<th className="text-muted pr-4 pb-1 text-xs font-normal">
													Name
												</th>
												<th className="text-muted pb-1 text-xs font-normal">
													Content
												</th>
											</tr>
										</thead>
										<tbody>
											{Object.entries(
												result.data.meta,
											).map(([name, content]) => (
												<tr
													key={name}
													className="border-b border-white/5"
												>
													<td className="text-muted py-1 pr-4 font-mono text-xs">
														{name}
													</td>
													<td className="py-1 text-xs break-all">
														{content}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								) : (
									<p className="text-muted text-xs">
										No meta tags found.
									</p>
								)}
							</div>
						</div>

						<div className="rounded border border-white/10">
							<div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
								<h2 className="text-sm font-medium">Content</h2>
								{viewToggle}
							</div>
							{view === "rendered" ? (
								<div className="max-h-[600px] overflow-auto p-6">
									<MarkdownRenderer
										markdown={contentText ?? ""}
									/>
								</div>
							) : view === "preview" ? (
								<iframe
									srcDoc={
										contentText
											? stripImages(contentText)
											: ""
									}
									sandbox=""
									className="h-[600px] w-full bg-white"
									title="Preview"
								/>
							) : (
								<div className="max-h-[600px] overflow-auto p-4">
									<pre className="text-xs leading-relaxed">
										<code className="break-words whitespace-pre-wrap">
											{contentText}
										</code>
									</pre>
								</div>
							)}
						</div>
					</div>

					<SaveFixturePanel
						key={result.data.url}
						url={result.data.url}
						data={result.data}
					/>
				</>
			)}

			{result.status !== "loading" && <ComparePanel />}
		</div>
	);
}

function deriveFixtureName(url: string): string {
	const u = new URL(url);
	const host = u.hostname.replace(/^www\./, "").replace(/\./g, "-");
	const pathSlug = u.pathname.split("/").filter(Boolean)[0] || "";
	return pathSlug ? `${host}-${pathSlug}` : host;
}

function SaveFixturePanel({ url, data }: { url: string; data: ScrapeData }) {
	const [name, setName] = useState(() => deriveFixtureName(url));
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function handleSave() {
		setSaving(true);
		setSaved(null);
		setError(null);
		try {
			const res = await fetch("/dev-api/save-fixture", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name,
					url,
					rawHtml: data.html,
					expectedMarkdown: data.markdown,
				}),
			});
			const json = await res.json();
			if (!res.ok || json.error) {
				setError(json.error || `HTTP ${res.status}`);
			} else {
				setSaved(json.data.path);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="rounded border border-white/10">
			<div className="border-b border-white/10 px-4 py-2">
				<h2 className="text-sm font-medium">Save Fixture</h2>
			</div>
			<div className="flex items-center gap-2 p-4">
				<input
					type="text"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="fixture-name"
					className="flex-1 rounded border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-sm placeholder:text-white/30 focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/30 focus:outline-none"
				/>
				<button
					onClick={handleSave}
					disabled={saving || !name.trim()}
					className="rounded bg-yellow-500 px-4 py-1.5 text-sm font-medium text-black hover:bg-yellow-400 disabled:opacity-40"
				>
					{saving ? "Saving..." : "Save Fixture"}
				</button>
			</div>
			{saved && (
				<div className="border-t border-white/5 px-4 py-2">
					<p className="text-xs text-green-400">
						Saved to packages/lib/__tests__/fixtures/{name}/
					</p>
				</div>
			)}
			{error && (
				<div className="border-t border-white/5 px-4 py-2">
					<p className="text-xs text-red-400">{error}</p>
				</div>
			)}
		</div>
	);
}

function ComparePanel() {
	const [cleanHtml, setCleanHtml] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function handlePasteAndCompare() {
		const text = await navigator.clipboard.readText();
		if (!text.trim()) return;
		setLoading(true);
		try {
			const res = await fetch("/dev-api/clean-html", {
				method: "POST",
				body: text,
			});
			const json = await res.json();
			setCleanHtml(json.data.cleanHtml);
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="mt-8 rounded border border-white/10">
			<div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
				<h2 className="text-sm font-medium">Clean Browser HTML</h2>
				<p className="text-muted text-xs">
					Run{" "}
					<code className="rounded bg-white/10 px-1 py-0.5">
						copy(document.documentElement.outerHTML)
					</code>{" "}
					in the browser console first
				</p>
			</div>
			<div className="p-4">
				<div className="flex items-center gap-2">
					<button
						onClick={handlePasteAndCompare}
						disabled={loading}
						className="rounded bg-yellow-500 px-4 py-2 text-sm font-medium text-black hover:bg-yellow-400 disabled:opacity-40"
					>
						{loading ? "Cleaning..." : "Paste & Clean"}
					</button>
					{cleanHtml && <CopyButton text={cleanHtml} />}
				</div>
				{cleanHtml != null && (
					<div className="mt-4 max-h-[600px] overflow-auto rounded border border-white/10 p-4">
						<pre className="text-xs leading-relaxed">
							<code className="break-words whitespace-pre-wrap">
								{cleanHtml}
							</code>
						</pre>
					</div>
				)}
			</div>
		</div>
	);
}

function stripImages(html: string): string {
	return html.replace(/<img[^>]*>/gi, "");
}

function stripAttributes(html: string): string {
	return html.replace(
		/\s+(?:class|role|tabindex|aria-[\w-]+)=(?:"[^"]*"|'[^']*')/gi,
		"",
	);
}

function CopyButton({ text }: { text: string }) {
	const [copied, setCopied] = useState(false);

	async function handleCopy() {
		await navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	}

	return (
		<button
			onClick={handleCopy}
			className="text-muted hover:text-foreground p-1"
			title="Copy to clipboard"
		>
			{copied ? (
				<span className="text-xs text-green-400">Copied</span>
			) : (
				<FiCopy size={14} />
			)}
		</button>
	);
}
